import { useMutation } from '@tanstack/react-query';
import { getConfigService, getTokenService } from '../../../../services/angular-bridge';
import { ensureFreshToken } from '../../../../services/api';

function buildSourceQueryString(sourceConfig: Record<string, any>): string {
  const params = new URLSearchParams();
  Object.entries(sourceConfig).forEach(([k, v]) => {
    if (v !== undefined && v !== null && k !== 'files' && k !== 'getListings') {
      params.set(k, String(v));
    }
  });
  return params.toString();
}

type ProgressCallback = (loaded: number, total: number, percent: number) => void;

interface ProcessParams {
  projectId: string;
  collectionId: string;
  sourceConfig: Record<string, any>;
  file?: File | null;
  onProgress?: ProgressCallback;
}

function processWithXhr(url: string, file: File | null | undefined, onProgress: ProgressCallback | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    const token = getTokenService().GetAccessToken?.();
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    if (file) formData.append('file', file);

    if (xhr.upload && onProgress) {
      xhr.upload.addEventListener('progress', ev => {
        if (ev.lengthComputable) onProgress(ev.loaded, ev.total, Math.floor(ev.loaded * 100 / ev.total));
      });
    }

    xhr.open('POST', url);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`${xhr.status}`)));
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(formData);
  });
}

export function useProcessCollection() {
  return useMutation({
    mutationFn: async ({ projectId, collectionId, sourceConfig, file, onProgress }: ProcessParams) => {
      // The upload body is the file; a 401 mid-flight would mean re-sending it. Refresh
      // up front so the XHR starts with a valid token.
      await ensureFreshToken();
      const qs = buildSourceQueryString(sourceConfig);
      const base = getConfigService().getServerBaseUrl();
      const url = `${base}/project/${projectId}/collection/${collectionId}/process?${qs}`;
      return processWithXhr(url, file, onProgress);
    },
  });
}
